import React, { useCallback, useEffect, useState } from 'react';
import { Button, Spinner } from 'react-bootstrap';
import { toast } from 'sonner';
import { Brain, RefreshCw } from 'lucide-react';

import { useFeatures } from '../../../context/FeatureContext';
import { getEmbeddingStats, rebuildAiHistory } from '../../../services/MatchingService';

/**
 * "Bygg upp AI-historik" — self-service rebuild of the tenant's vector corpus
 * from its existing bookkeeping (design §4.6 §2). For companies that imported
 * their SIE files BEFORE AI matching existed, so they have full MySQL history
 * but no vectors and would otherwise start cold.
 *
 * Self-contained + gated: renders only when the tenant has `ai_reconciliation`
 * AND the vector stack is live in this environment (embedding-stats `live`).
 * The rebuild is idempotent server-side. The global-priors opt-out toggle rides
 * along in the same card (it self-hides when `global_priors` is absent).
 */
export default function RebuildAiHistoryControl({ companyId }) {
    const { features } = useFeatures();
    const enabled = Boolean(features?.ai_reconciliation);

    const [stats, setStats] = useState(null);
    const [hidden, setHidden] = useState(false);
    const [busy, setBusy] = useState(false);

    const loadStats = useCallback(async () => {
        if (!companyId) return;
        try {
            const data = await getEmbeddingStats(companyId);
            setStats(data);
            // Hide when the vector stack isn't live in this env — a rebuild
            // would no-op, so there's nothing meaningful to offer.
            setHidden(!data?.live);
        } catch {
            setHidden(true);
        }
    }, [companyId]);

    useEffect(() => {
        if (enabled) loadStats();
    }, [enabled, loadStats]);

    const handleRebuild = useCallback(async () => {
        setBusy(true);
        try {
            const data = await rebuildAiHistory(companyId);
            if (data?.mode === 'too_large') {
                toast.message('Din historik är stor — kontakta support för en fullständig ombyggnad.');
            } else if (data?.mode === 'skipped') {
                toast.error('AI-historik är inte tillgänglig just nu.');
            } else {
                toast.success(`AI-historik uppbyggd — ${data?.upserted ?? 0} transaktioner indexerade.`);
            }
            if (data?.stats) {
                setStats((prev) => ({ ...(prev || {}), ...data.stats }));
            } else {
                loadStats();
            }
        } catch (err) {
            toast.error(err?.response?.data?.message || err.message || 'Kunde inte bygga upp AI-historik');
        } finally {
            setBusy(false);
        }
    }, [companyId, loadStats]);

    if (!enabled || hidden) return null;

    const total = stats?.vectors_total ?? 0;
    const bySie = stats?.by_source?.sie ?? 0;
    const byBooking = stats?.by_source?.booking ?? 0;

    return (
        <div className="modern-card" id="ai-historik">
            <div className="modern-card-header">
                <div>
                    <h2 className="section-title">
                        <Brain size={24} />
                        AI-historik
                    </h2>
                    <p className="section-subtitle">
                        Låt AI:n lära sig hur ni brukar bokföra utifrån er befintliga historik.
                        Användbart om ni importerade bokföringen innan AI-matchningen aktiverades.
                    </p>
                </div>
                <div className="action-buttons">
                    <Button
                        variant="primary"
                        onClick={handleRebuild}
                        disabled={busy}
                        className="d-flex align-items-center gap-2"
                    >
                        {busy ? (
                            <><Spinner animation="border" size="sm" /> Bygger…</>
                        ) : (
                            <><RefreshCw size={16} /> Bygg upp AI-historik</>
                        )}
                    </Button>
                </div>
            </div>

            <div className="modern-card-body">
                <p className="mb-3" style={{ fontSize: '0.9rem', color: '#4b5563' }}>
                    {total > 0 ? (
                        <>Just nu är <strong>{total}</strong> transaktioner indexerade
                        {' '}({byBooking} bokförda, {bySie} från SIE).</>
                    ) : (
                        <>Inga transaktioner är indexerade än. Klicka på knappen för att bygga upp historiken
                        från er bokföring. Det går att köra om när som helst — det skapar inga dubbletter.</>
                    )}
                </p>
                {/* Phase D: the "Dela anonym statistik" toggle moved to its
                    own card in Inställningar → Automatisering — exactly one
                    place for the control. */}
            </div>
        </div>
    );
}
